import React, { useState } from 'react';
import { Tooltip, Popover, Tag, Button } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, CustomerServiceOutlined, DownOutlined, MedicineBoxOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import moment from 'moment';

import DropdownActions from './DropdownActions';
import PhoneNumberList from '@/components/PhoneNumberList';
import TextTruncate from 'react-text-truncate';
import HtmlParser from 'react-html-parser';
import { ConvertCustomerScheduleType, isValidDate, RenderCustomerServiceStatus, stripHtml } from '@/utils/helpers';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import NumberFormat from 'react-number-format';


/** utils nhỏ */
const byKey = (arr) => Object.fromEntries((arr || []).map(c => [c?.key, c]));
const mergeCol = (base, patch) => ({ ...base, ...patch });
const insertExtras = (cols, extras = []) => {
    const out = [...cols];
    for (const ex of extras) {
        if (!ex?.key || !ex?.column) continue;
        // remove nếu đã tồn tại
        const exists = out.findIndex(c => c?.key === ex.key);
        if (exists >= 0) out.splice(exists, 1);

        const { insert = {} } = ex;
        const { beforeKey, afterKey, atIndex } = insert;

        if (typeof atIndex === 'number') {
            out.splice(Math.max(0, Math.min(atIndex, out.length)), 0, ex.column);
        } else if (beforeKey) {
            const idx = out.findIndex(c => c?.key === beforeKey);
            out.splice(idx >= 0 ? idx : out.length, 0, ex.column);
        } else if (afterKey) {
            const idx = out.findIndex(c => c?.key === afterKey);
            out.splice(idx >= 0 ? idx + 1 : out.length, 0, ex.column);
        } else {
            out.push(ex.column);
        }
    }
    return out;
};

/**
 * Tạo danh sách cột cho bảng khách hàng + hỗ trợ tuỳ biến ẩn/hiện/override/chèn thêm
 *
 * @param {Object} ctx
 *  - allowDetail?: boolean (default true)
 *  - onOpenScheduleDetail?: (customerId:number|string)=>void
 *
 * @param {Object} options
 *  - visibleKeys?: string[]      // chỉ hiển thị các key này (ưu tiên)
 *  - hiddenKeys?: string[]       // ẩn các key này (nếu không dùng visibleKeys)
 *  - overrides?: { [key]: Partial<Column> } // ghi đè props cột theo key
 *  - extras?: { key:string, column:Object, insert?:{ beforeKey?:string, afterKey?:string, atIndex?:number } }[]
 *
 * @returns {Array} AntD columns
 */
export function buildCustomerColumns(ctx = {}, options = {}) {
    const { onOpenScheduleDetail, loadData, showCardNumber } = ctx;

    const {
        visibleKeys,
        hiddenKeys = [],
        overrides = {},
        extras = [],
    } = options;

    const serviceTitleStyle = {
        fontSize: 13,
        fontWeight: 600,
        color: '#262626'
    };

    const statusStyle = {
        fontSize: 12,
        fontWeight: 400,
        color: '#8c8c8c'
    };

    const metaStyle = {
        fontSize: 12,
        color: '#8c8c8c',
        marginBottom: 4
    };

    const sectionLabelStyle = {
        fontWeight: 600,
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        color: '#595959',
        marginRight: 4
    };

    const rolePrefixStyle = {
        fontWeight: 400,
        color: '#8c8c8c'
    };

    const staffNameStyle = {
        fontWeight: 600,
        color: '#111',
        fontSize: 14
    };


    const labelIconStyle = {
        fontSize: 14,
        color: '#8c8c8c',
        marginRight: 6
    };


    const CustomerCareCell = ({ logs = [], limit = 1 }) => {
        const [expanded, setExpanded] = useState(false);

        if (!Array.isArray(logs) || logs.length === 0) {
            return <span style={{ color: '#bfbfbf' }}>—</span>;
        }

        const sortedLogs = [...logs].sort(
            (a, b) => new Date(b.called_at) - new Date(a.called_at)
        );

        const visibleLogs = expanded
            ? sortedLogs
            : sortedLogs.slice(0, limit);

        return (
            <div style={{ lineHeight: 1.5 }}>
                {visibleLogs.map((log, index) => (
                    <div
                        key={log.id}
                        style={{
                            padding: '8px 0 8px 8px',
                            borderLeft: '3px dashed #e6f4ff',
                            marginLeft: "-8px",
                            //marginBottom: 8,
                            borderBottom:
                                index < sortedLogs.length - 1
                                    ? '2px dashed #e6f4ff'
                                    : 'none'
                        }}
                    >
                        {/* Nội dung CSKH */}
                        <div
                            style={{
                                marginBottom: 4,
                            }}
                        >
                            {HtmlParser(log.customer_feedback)}
                        </div>

                        {/* Thủ thuật */}
                        {log.categories?.length > 0 && (
                            <div style={{ marginBottom: 4 }}>
                                {log.categories.map(cat => (
                                    <Tag
                                        key={cat.id}
                                        color="blue"
                                        style={{
                                            background: '#f0f5ff',
                                            color: '#2f54eb',
                                            border: '1px solid #adc6ff',
                                            marginBottom: 4,
                                        }}
                                    >
                                        {cat.name}
                                    </Tag>
                                ))}
                            </div>
                        )}

                        {/* Meta */}
                        <div
                            style={{
                                fontSize: 13,
                                color: '#8c8c8c',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            <span>
                                <UserOutlined /> {log.staff?.full_name || '—'}
                            </span>
                            <span>
                                <ClockCircleOutlined /> {isValidDate(log.called_at, true)}
                            </span>
                        </div>
                    </div>
                ))}

                {/* Toggle */}
                {sortedLogs.length > limit && (
                    <div>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, fontSize: 13 }}
                            onClick={() => setExpanded(!expanded)}
                        >
                            <span style={{ color: 'blue' }}>
                                {expanded
                                    ? 'Thu gọn'
                                    : `Xem thêm (${sortedLogs.length - limit})`}
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const ServiceColumnCell = ({ services = [], limit = 2 }) => {
        const [expanded, setExpanded] = useState(false);

        if (!Array.isArray(services) || services.length === 0) {
            return <span style={{ color: '#bfbfbf' }}>—</span>;
        }

        const visibleServices = expanded
            ? services
            : services.slice(0, limit);

        return (
            <div style={{ lineHeight: 1.6 }}>
                {visibleServices.map((sv, index) => {
                    const serviceName = sv.service?.name || '—';
                    const statusLabel = RenderCustomerServiceStatus(sv.status) || '—';
                    const selectedAt = sv.selected_at
                        ? isValidDate(sv.selected_at, false)
                        : '—';

                    /* ===== Điều trị ===== */
                    const exams = sv.exam_visit || [];

                    const doctors = [
                        ...new Set(exams.map(e => e.doctor_charge_id).filter(Boolean))
                    ];

                    const assistants = [
                        ...new Set(exams.map(e => e.assistant_doctor_id).filter(Boolean))
                    ];

                    /* ===== Tư vấn – 1 dòng, giữ role ===== */
                    const consultantLine = (sv.staff_consultant || [])
                        .map(c => {
                            const role = c.employee?.user_role?.name;
                            const name = c.employee?.full_name;
                            if (!role || !name) return null;
                            return `${role}: ${name}`;
                        })
                        .filter(Boolean)
                        .join('; ');

                    return (
                        <div key={sv.id}
                            style={{
                                padding: '8px 0 8px 8px',
                                borderLeft: '3px solid #e6f4ff',
                                marginLeft: "-8px",
                                //marginBottom: 8,
                                borderBottom:
                                    index < services.length - 1
                                        ? '2px dashed #e6f4ff'
                                        : 'none'
                            }}
                        >
                            {/* ===== Header ===== */}
                            <div style={serviceTitleStyle}>
                                {serviceName}{' '}
                                <span style={statusStyle}>· {statusLabel}</span>
                            </div>

                            {/* ===== Ngày chốt ===== */}
                            <div style={metaStyle}>
                                <ClockCircleOutlined style={labelIconStyle} />
                                Ngày chốt: {selectedAt}
                            </div>

                            {/* ===== BÁC SĨ ===== */}
                            <div>
                                <MedicineBoxOutlined style={labelIconStyle} />
                                <span style={sectionLabelStyle}>Bác sĩ:</span>
                                <span style={staffNameStyle}>
                                    {doctors.length ? doctors.join(', ') : '—'}
                                </span>
                            </div>

                            {/* ===== PHỤ TÁ ===== */}
                            <div>
                                <TeamOutlined style={labelIconStyle} />
                                <span style={sectionLabelStyle}>Phụ tá:</span>
                                <span style={staffNameStyle}>
                                    {assistants.length ? assistants.join(', ') : '—'}
                                </span>
                            </div>

                            {/* ===== TƯ VẤN ===== */}
                            {consultantLine && (
                                <div>
                                    <CustomerServiceOutlined style={labelIconStyle} />
                                    <span style={sectionLabelStyle}>Tư vấn:</span>

                                    <span>
                                        {consultantLine.split('; ').map((item, idx) => {
                                            const [role, name] = item.split(': ');
                                            return (
                                                <span key={idx}>
                                                    <span style={rolePrefixStyle}>
                                                        {role}:
                                                    </span>{' '}
                                                    <span style={staffNameStyle}>
                                                        {name}
                                                    </span>
                                                    {idx < consultantLine.split('; ').length - 1 && '; '}
                                                </span>
                                            );
                                        })}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* ===== TOGGLE ===== */}
                {services.length > limit && (
                    <div>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, fontSize: 13 }}
                            onClick={() => setExpanded(!expanded)}
                        >
                            <span style={{ color: 'blue' }}>
                                {expanded
                                    ? 'Thu gọn'
                                    : `Xem thêm (${services.length - limit})`}
                            </span>
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const getNonEmptyNames = (items, getName) => (
        (items || [])
            .map(getName)
            .filter(Boolean)
    );

    const normalizeTreatmentSessions = (treatmentSessions) => {
        if (!treatmentSessions) return [];
        return Array.isArray(treatmentSessions) ? treatmentSessions : [treatmentSessions];
    };

    const treatmentStyles = {
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            lineHeight: 1.5,
            fontSize: 14,
            color: '#262626',
        },
        session: {
            padding: '6px 0',
            borderBottom: '1px solid #f0f0f0',
        },
        doctorRole: {
            color: '#999',
            fontSize: 14,
        },
        doctorName: {
            fontSize: 14,
            fontWeight: 700,
        },
        summaryLine: {
            marginTop: 5,
        },
        summaryLabel: {
            color: '#595959',
            fontWeight: 600,
            fontSize: 14,
            marginRight: 4,
        },
        summaryText: {
            color: '#262626',
            fontSize: 14,
        },
        tags: {
            display: 'inline-flex',
            flexWrap: 'wrap',
            gap: 4,
            verticalAlign: 'top',
        },
        itemTag: {
            margin: 0,
            whiteSpace: 'normal',
            lineHeight: 1.45,
            fontSize: 13,
            border: 0
        },
        empty: {
            color: '#bfbfbf',
            fontSize: 14,
        },
    };

    const renderTreatmentDoctor = (doctor) => {
        if (!doctor?.full_name) return <span style={treatmentStyles.empty}>----</span>;

        const roleName = doctor.user_rolename || doctor.user_role?.name || '';

        return (
            <div>
                {roleName && (
                    <span style={treatmentStyles.doctorRole}>
                        {roleName} -{' '}
                    </span>
                )}
                <span style={treatmentStyles.doctorName}>{doctor.full_name}</span>
            </div>
        );
    };

    const renderCompactLine = (label, items) => (
        items.length ? (
            <div style={treatmentStyles.summaryLine}>
                <span style={treatmentStyles.summaryLabel}>{items.length} {label}:</span>
                <span style={treatmentStyles.tags}>
                    {items.map((item, index) => (
                        <Tag key={`${item}-${index}`} color="blue" style={treatmentStyles.itemTag}>
                            {item}
                        </Tag>
                    ))}
                </span>
            </div>
        ) : null
    );

    const renderTextLine = (label, value) => (
        value ? (
            <div style={treatmentStyles.summaryLine}>
                <span style={treatmentStyles.summaryLabel}>{label}:</span>
                <span style={treatmentStyles.summaryText}>{value}</span>
            </div>
        ) : null
    );

    const renderTreatmentTicket = (record) => {
        const treatmentSessions = normalizeTreatmentSessions(
            record?.treatment_sessions || record?.treatmentSessions
        );

        if (!treatmentSessions.length) {
            return <span style={treatmentStyles.empty}>----</span>;
        }

        return (
            <div style={treatmentStyles.wrapper}>
                {treatmentSessions.map((session, index) => {
                    const procedures = getNonEmptyNames(
                        session?.procedures,
                        (procedure) => procedure?.name
                    );
                    const services = getNonEmptyNames(
                        session?.customer_services || session?.customerServices,
                        (item) => item?.customer_service?.service?.name || item?.customerService?.service?.name
                    );
                    const note = session?.ghi_chu_noi_bo;
                    const treatmentDate = isValidDate(session?.ngay, false);
                    const hasContent = treatmentDate || note || session?.doctor?.full_name || procedures.length || services.length;

                    return (
                        <div
                            key={session?.id || index}
                            style={{
                                ...treatmentStyles.session,
                                borderBottom: index === treatmentSessions.length - 1 ? 0 : treatmentStyles.session.borderBottom,
                            }}
                        >
                            {hasContent ? (
                                <>
                                    {renderTextLine('Ngày điều trị', treatmentDate)}
                                    {session?.doctor?.full_name && renderTreatmentDoctor(session?.doctor)}
                                    {renderCompactLine('thủ thuật', procedures)}
                                    {renderCompactLine('dịch vụ', services)}
                                    {note && (
                                        <div style={treatmentStyles.summaryLine}>
                                            <span style={treatmentStyles.summaryLabel}>Ghi chú:</span>
                                            <span style={treatmentStyles.summaryText}>{HtmlParser(note)}</span>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <span style={treatmentStyles.empty}>----</span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    /** 1) Cột gốc */
    const base = [
        {
            title: '#',
            align: 'center',
            width: '50px',
            dataIndex: 'actions',
            key: 'actions',
            render: (text, record) => <DropdownActions record={record} loadData={loadData} />,
        },
        {
            title: 'Họ và tên',
            dataIndex: 'full_name',
            key: 'full_name',
            width: '250px',
            render: (_, record) => {
                const customer = {
                    id: record?.id,
                    code: record?.code,
                    full_name: record?.full_name,
                    branch_name: record?.branche?.name,
                    conditions: record?.conditions,
                    customer_source: record?.customer_source?.name,
                    customer_status: record?.customer_status?.name,
                    customer_status_color: record?.customer_status?.text_color,
                    medical_book_number: record?.medical_book_number,
                    card_number: record?.card_number_new
                }

                return (
                    <CustomerInfoColumn
                        record={customer}
                        nameField="full_name"          // field tuỳ module
                        idField="id"
                        codeField="code"
                        branchField="branch_name"
                        medicalBookField="medical_book_number"
                        conditionField="conditions"
                        sourceField="customer_source"
                        statusField="customer_status"
                        statusColorField="customer_status_color"
                        cardNumberField="card_number"
                        showCardNumber={showCardNumber}
                    />
                )
            }
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'birthday',
            key: 'birthday',
            width: 120,
            render: (text, record) => {
                if (!text) return <span style={{ color: '#bfbfbf' }}>—</span>;

                const birthDate = moment(text);
                const isValid = birthDate.isValid();
                if (!isValid) return <span style={{ color: '#bfbfbf' }}>—</span>;

                const age = record.age;
                const formattedDate = isValidDate(record.birthday, false);

                return (
                    <div style={{ lineHeight: 1.3 }}>
                        <div style={{ fontWeight: 500, color: '#222' }}>
                            {formattedDate}
                        </div>
                        {age && (
                            <div style={{ fontSize: 14, color: '#8c8c8c', marginTop: 2 }}>
                                {age} tuổi
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Nickname',
            dataIndex: 'fb_name',
            key: 'fb_name',
            width: '180px',
            render: (text, record) => <>{record.fb_name}</>,
        },
        {
            title: ' Số điện thoại',
            dataIndex: 'phone',
            key: 'phone',
            width: '120px',
            render: (text, record) => <PhoneNumberList phones={record.phones} />,
        },
        // {
        //     title: 'Lịch hẹn gần nhất',
        //     dataIndex: 'appointments',
        //     key: 'appointments',
        //     width: '260px',
        //     render: (appointments, record) => {
        //         if (!Array.isArray(appointments) || !appointments.length) return null;

        //         const sortedAppointments = [...appointments].sort((a, b) => {
        //             const timeA = new Date(a.time || 0).getTime();
        //             const timeB = new Date(b.time || 0).getTime();
        //             return timeB - timeA;
        //         });

        //         const firstAppointment = sortedAppointments[0];
        //         const remainCount = sortedAppointments.length - 1;

        //         const scheduleTime = firstAppointment?.time;
        //         const note = firstAppointment?.note || '';

        //         return (
        //             <div>

        //                 {/* CARD LỊCH */}
        //                 <div
        //                     style={{
        //                         lineHeight: 1.6,
        //                         fontSize: 14,
        //                         padding: '8px 0 8px 8px',
        //                         borderLeft: '3px solid #e6f4ff',
        //                         marginLeft: '-8px',
        //                     }}
        //                 >

        //                     {/* Ngày giờ */}
        //                     <div style={{ marginBottom: 4 }}>
        //                         <CalendarOutlined style={{ color: '#1677ff', marginRight: 6 }} />
        //                         <span style={{ fontWeight: 600, color: '#1677ff' }}>
        //                             {scheduleTime ? moment(scheduleTime).format('DD/MM/YYYY HH:mm') : '-'}
        //                         </span>
        //                     </div>

        //                     {/* Nội dung */}
        //                     <div style={{ marginBottom: 4 }}>
        //                         <span style={{ fontWeight: 500 }}>Nội dung:</span>{' '}
        //                         <span style={{ color: '#222' }}>
        //                             {ConvertCustomerScheduleType(firstAppointment.name)}
        //                         </span>
        //                     </div>

        //                     {/* Ghi chú */}
        //                     {note && (
        //                         <div>
        //                             <span style={{ fontWeight: 500 }}>Ghi chú:</span>{' '}
        //                             <span style={{ color: '#888' }}>
        //                                 {note}
        //                             </span>
        //                         </div>
        //                     )}

        //                 </div>

        //                 {/* XEM THÊM */}
        //                 {remainCount > 0 && (
        //                     <div style={{ marginTop: 2 }}>
        //                         <div>
        //                             <Button
        //                                 type="link"
        //                                 size="small"
        //                                 style={{ padding: 0, fontSize: 13 }}
        //                                 onClick={() => onOpenScheduleDetail?.(record)}
        //                             >
        //                                 <span style={{ color: 'blue' }}>
        //                                     Xem thêm ({remainCount}) lịch
        //                                 </span>
        //                             </Button>
        //                         </div>
        //                     </div>
        //                 )}

        //             </div>
        //         );
        //     },
        // },
        {
            title: 'Lịch hẹn gần nhất',
            dataIndex: 'appointments',
            key: 'appointments',
            width: '260px',
            render: (appointments, record) => {
                if (!Array.isArray(appointments) || !appointments.length) return null;

                const sortedAppointments = [...appointments].sort((a, b) => {
                    const timeA = new Date(a.time || 0).getTime();
                    const timeB = new Date(b.time || 0).getTime();
                    return timeB - timeA;
                });

                const firstAppointment = sortedAppointments[0];
                const remainCount = sortedAppointments.length - 1;

                const scheduleTime = firstAppointment?.time;
                const note = firstAppointment?.note || '';

                const doctors = Array.isArray(firstAppointment?.doctors)
                    ? firstAppointment.doctors
                    : [];

                const doctorNames = doctors
                    .map(item => item?.full_name)
                    .filter(Boolean)
                    .join(', ');

                return (
                    <div>
                        {/* CARD LỊCH */}
                        <div
                            style={{
                                lineHeight: 1.6,
                                fontSize: 14,
                                padding: '8px 0 8px 8px',
                                borderLeft: '3px solid #e6f4ff',
                                marginLeft: '-8px',
                            }}
                        >
                            {/* Ngày giờ */}
                            <div style={{ marginBottom: 6 }}>
                                <CalendarOutlined style={{ color: '#1677ff', marginRight: 6 }} />
                                <span style={{ fontWeight: 600, color: '#1677ff' }}>
                                    {scheduleTime ? moment(scheduleTime).format('DD/MM/YYYY HH:mm') : '-'}
                                </span>
                            </div>

                            {/* Bác sĩ */}
                            {!!doctorNames && (
                                <div>
                                    <span style={{ fontWeight: 500 }}>Bác sĩ:</span>{' '}
                                    <span style={{ ...staffNameStyle, color: '#888' }}>
                                        {doctorNames}
                                    </span>
                                </div>
                            )}

                            {/* Nội dung */}
                            <div style={{ marginBottom: note ? 4 : 0 }}>
                                <span style={{ fontWeight: 500 }}>Nội dung:</span>{' '}
                                <span style={{ color: '#222' }}>
                                    {ConvertCustomerScheduleType(firstAppointment.name)}
                                </span>
                            </div>

                            {/* Ghi chú */}
                            {note && (
                                <div>
                                    <span style={{ fontWeight: 500 }}>Ghi chú:</span>{' '}
                                    <span style={{ color: '#888' }}>{note}</span>
                                </div>
                            )}
                        </div>

                        {/* XEM THÊM */}
                        {remainCount > 0 && (
                            <div style={{ marginTop: 2 }}>
                                <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0, fontSize: 13 }}
                                    onClick={() => onOpenScheduleDetail?.(record)}
                                >
                                    <span style={{ color: 'blue' }}>
                                        Xem thêm ({remainCount}) lịch
                                    </span>
                                </Button>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Phiếu điều trị',
            dataIndex: 'treatment_note',
            key: 'treatment_note',
            width: '350px',
            render: (_, record) => renderTreatmentTicket(record)
        },
        // {
        //     title: 'Lịch hẹn',
        //     dataIndex: 'appointments',
        //     key: 'appointments',
        //     width: '180px',
        //     render: (text, record) => {
        //         if (!Array.isArray(text) || !text.length) return null;

        //         const content = text.map((item, index) => (
        //             <div key={index} style={{ padding: '5px 0' }}>
        //                 {moment(item.time).format('DD/MM/YYYY HH:mm')}
        //             </div>
        //         ));

        //         return (
        //             <div className="item-action-btn" onClick={() => onOpenScheduleDetail?.(record)}>
        //                 <Popover placement="right" content={content}>
        //                     {moment(text[0]?.time).format('DD/MM/YYYY HH:mm')} <DownOutlined />
        //                 </Popover>
        //             </div>
        //         );
        //     },
        // },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            width: '250px',
            render: (text) => (
                <div style={{ wordBreak: 'break-word' }}>
                    <Tooltip title={HtmlParser(text)} placement="topLeft">
                        <TextTruncate line={5} truncateText="…" text={stripHtml(text)} />
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Kết quả CSKH',
            key: 'customer_care_logs',
            dataIndex: 'customer_care_logs',
            width: 420,
            render: (logs) => (
                <CustomerCareCell logs={logs} limit={1} />
            )
        },
        {
            key: 'service_info',
            title: 'Dịch vụ',
            width: 390,
            render: (_, record) => (
                <ServiceColumnCell services={record.service_used} limit={1} />
            )
        },
        {
            title: 'Doanh số & công nợ',
            key: 'revenue_summary',
            width: 220,
            sorter: true,
            align: 'right',
            render: (_, record) => {
                const revenue = Number(record.revenue || 0);
                const paid = Number(record.total_paid_amount || 0);
                const debt = record.debt || 0;
                if (revenue === 0) {
                    return <Tag></Tag>;
                }

                const paidPercentRaw = (paid / revenue) * 100;
                const paidPercent = Math.round(paidPercentRaw * 10) / 10;

                let percentColor = '#1677ff'; // >= 100% (xanh dương)
                if (paidPercent < 50) {
                    percentColor = '#fa8c16';   // tím đậm – thu kém (cảnh báo)
                } else if (paidPercent < 100) {
                    percentColor = '#531dab';   // cam – đang thu
                }

                return (
                    <div style={{ textAlign: 'right', lineHeight: 1.6 }}>
                        {/* Doanh số */}
                        <div style={{ fontWeight: 600 }}>
                            <NumberFormat
                                value={revenue}
                                displayType="text"
                                thousandSeparator
                                suffix=" đ"
                            />
                        </div>

                        {/* Đã thanh toán */}
                        <div style={{ fontSize: 12, color: '#389e0d', fontWeight: 500 }}>
                            Đã thu:{' '}
                            <NumberFormat
                                value={paid}
                                displayType="text"
                                thousandSeparator
                                suffix=" đ"
                            />
                        </div>

                        {/* Tỷ lệ thu */}
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: percentColor
                            }}
                        >
                            Tỷ lệ thu: {paidPercent}%
                        </div>

                        {/* Còn nợ */}
                        {debt > 0 && (
                            <div style={{ fontSize: 12, color: '#cf1322', fontWeight: 500 }}>
                                Còn nợ:{' '}
                                <NumberFormat
                                    value={debt}
                                    displayType="text"
                                    thousandSeparator
                                    suffix=" đ"
                                />
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '220px',
            sorter: true,
            render: (text, record) => {
                const staff = record?.created_user;
                return (
                    <>
                        <div>{isValidDate(text, true)}</div>

                        {staff && staff.full_name && (
                            <div>
                                {staff.user_role?.name && (
                                    <span style={{ color: "#999", fontSize: 13.5 }}>
                                        {staff.user_role.name}:{' '}
                                    </span>
                                )}
                                <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                                    {staff.full_name}
                                </span>
                            </div>
                        )}
                    </>
                );
            }
        }
    ];

    /** 2) Override props theo key (nếu có) */
    const mapBase = byKey(base);
    const merged = base.map(col => (col?.key && overrides[col.key]) ? mergeCol(col, overrides[col.key]) : col);

    /** 3) Chèn thêm cột (extras) nếu cần */
    const withExtras = insertExtras(merged, extras);

    /** 4) Lọc theo visibleKeys/hiddenKeys */
    const allKeys = withExtras.map(c => c?.key).filter(Boolean);

    let selectedKeys = allKeys;
    if (Array.isArray(visibleKeys) && visibleKeys.length) {
        // giữ thứ tự theo visibleKeys; bỏ key không tồn tại
        selectedKeys = visibleKeys.filter(k => allKeys.includes(k));
    } else if (Array.isArray(hiddenKeys) && hiddenKeys.length) {
        selectedKeys = allKeys.filter(k => !hiddenKeys.includes(k));
    }

    return withExtras.filter(c => selectedKeys.includes(c?.key));
}
